// Small color-contrast helpers — no dependency, just the WCAG relative-
// luminance formula. Used to guarantee text stays legible regardless of
// what hex a calendar happens to be colored in Google Calendar.

function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const num = parseInt(m[1], 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function rgbToHex({ r, g, b }) {
  const toHex = v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function relLuminance({ r, g, b }) {
  const [R, G, B] = [r, g, b].map(v => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function contrastRatio(rgb1, rgb2) {
  const L1 = relLuminance(rgb1);
  const L2 = relLuminance(rgb2);
  const [lighter, darker] = L1 > L2 ? [L1, L2] : [L2, L1];
  return (lighter + 0.05) / (darker + 0.05);
}

function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return { h, s, l };
}

function hslToRgb({ h, s, l }) {
  function hue2rgb(p, q, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return { r: r * 255, g: g * 255, b: b * 255 };
}

// Darkens `hex` (keeping its hue) until it hits at least 4.5:1 contrast
// against `bgHex`. Returns the original hex unchanged if it already passes.
// Falls back to a safe dark neutral if a hue genuinely can't get there.
export function ensureContrastText(hex, bgHex, minRatio = 4.5, fallback = '#2A4030') {
  const rgb = hexToRgb(hex);
  const bg = hexToRgb(bgHex);
  if (!rgb || !bg) return hex || fallback;
  if (contrastRatio(rgb, bg) >= minRatio) return hex;

  const hsl = rgbToHsl(rgb);
  const s = Math.max(hsl.s, 0.4); // keep some saturation so hue stays readable, not just gray
  for (let l = hsl.l; l >= 0.05; l -= 0.03) {
    const candidate = hslToRgb({ h: hsl.h, s, l });
    if (contrastRatio(candidate, bg) >= minRatio) return rgbToHex(candidate);
  }
  return fallback;
}

const CREAM = '#F0EBE3';

// A small set of muted, earthy accent hues — Grove's own palette extended
// with a few analogous tones, rather than the raw (often pastel/saturated)
// hex Google Calendar happens to assign. Each is pre-verified to clear AA
// text contrast against the cream card background. `hue` is the anchor used
// to match a calendar's own color to the closest-feeling entry here.
const GROVE_CAL_PALETTE = [
  { name: 'forest',      hue: 130, hex: '#586156' },
  { name: 'wine',        hue: 5,   hex: '#8B3A3A' },
  { name: 'terracotta',  hue: 20,  hex: '#8B5A3C' },
  { name: 'gold',        hue: 45,  hex: '#8A6D2E' },
  { name: 'teal',        hue: 175, hex: '#3F6B63' },
  { name: 'slate',       hue: 208, hex: '#3F5A72' },
  { name: 'plum',        hue: 300, hex: '#6B4670' },
  { name: 'rose',        hue: 340, hex: '#7A4A5C' },
].map(c => ({ ...c, hex: ensureContrastText(c.hex, CREAM) }));

function circularHueDistance(h1, h2) {
  const d = Math.abs(h1 - h2) % 360;
  return d > 180 ? 360 - d : d;
}

function hueOf(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  return rgbToHsl(rgb).h * 360;
}

// Assigns each calendar a color from GROVE_CAL_PALETTE — closest in hue to
// its own Google Calendar color (so a "blue" calendar lands on Grove's slate,
// a "red" one on wine, etc.), while guaranteeing no two calendars in the
// input list share a color as long as there are enough palette entries to go
// around (greedy nearest-hue-first-available, consuming the palette pool as
// it goes; recycles the full palette if the family has more calendars than
// palette entries — a rare case, and a repeat there is a lesser problem than
// running out of colors). `calendars` should be in a stable order (e.g. the
// order they appear in calendarMeta) so the same calendar gets the same
// color across reloads. Returns { [calendarId]: hex }.
export function assignCalendarColors(calendars) {
  const available = [...GROVE_CAL_PALETTE];
  const assigned = {};
  for (const cal of calendars) {
    if (!cal?.id) continue;
    const rawHue = cal.color ? hueOf(cal.color) : 0;
    const pool = available.length ? available : GROVE_CAL_PALETTE;
    let best = pool[0];
    let bestDist = Infinity;
    for (const c of pool) {
      const d = circularHueDistance(rawHue, c.hue);
      if (d < bestDist) { bestDist = d; best = c; }
    }
    assigned[cal.id] = best.hex;
    const idx = available.indexOf(best);
    if (idx !== -1) available.splice(idx, 1);
  }
  return assigned;
}
